import Grid from '@material-ui/core/Grid';
import { BaseComponent, IStateBase } from 'components/BaseComponent';
import FieldText from 'components/Field/Text';
import { SvgIcon } from 'components/SvgIcon';
import Translate from 'components/Translate';
import { IProductConfig } from 'interfaces/IProductData';
import { IUserAddressModel } from 'interfaces/IUserAddressModel';
import React from 'react';
import { Observable } from 'rxjs/Observable';
import { cartService } from 'services/CartService';
import { getAddress, ICEP } from 'services/LocationService';
import { paymentService } from 'services/PaymentService';

import CityState, { ICityState } from './CityState';
import SearchCEP from './SearchCEP';

interface IState extends IStateBase {
  model: IUserAddressModel;
  cepNotFound: boolean;
  config: IProductConfig;
}

export default class Br extends BaseComponent<IState> {
  public numberInputRef: React.RefObject<any>;

  private currentPostalCode: string;

  constructor(props: any) {
    super(props);
    this.state = { model: null, cepNotFound: false, config: null };
    this.numberInputRef = React.createRef();
  }

  componentDidMount() {
    this.bindModel(paymentService.getAddress());
    this.observeState({ config: null }, cartService.getConfig());

    paymentService
      .getAddress()
      .first()
      .filter(address => address && !!address.postalCode)
      .bindComponent(this)
      .subscribe(address => {
        if (address.postalCode.length == 9) {
          this.searchAddress(address.postalCode);
        }
      });
  }

  setPostalCode = (postalCode: string) => {
    paymentService.setAddressData({ postalCode });

    if (postalCode.length < 9 || postalCode === this.currentPostalCode) {
      return;
    }

    this.searchAddress(postalCode);

    this.currentPostalCode = postalCode;
  };

  searchAddress = (postalCode: string) => {
    getAddress(postalCode)
      .loader()
      .catch(() => Observable.empty<ICEP>())
      .switchMap(data => {
        !data.cep ? this.setState({ cepNotFound: true }) : this.setState({ cepNotFound: false });

        return Observable.of(data);
      })
      .bindComponent(this)
      .subscribe(data => {
        if (data.cep) {
          paymentService.setAddressData({
            postalCode: data.cep,
            street: data.street,
            district: data.neighborhood,
            city: data.city,
            province: data.state
          });
        }
      });
  };

  cepClosed = () => {
    setTimeout(() => {
      this.numberInputRef.current && this.numberInputRef.current.focus();
    }, 100);
  };

  setStreet = (street: any) => {
    paymentService.setAddressData({ street });
  };

  setNumber = (number: any) => {
    const stringNumber = `${number}` || '';
    const newString = stringNumber.replace(/[^a-zA-Z0-9]/gi, '').substring(0, 10);
    paymentService.setAddressData({ number: newString });
  };

  setDistrict = (district: any) => {
    paymentService.setAddressData({ district });
  };

  setComplement = (complement: any) => {
    paymentService.setAddressData({ complement });
  };

  setAddressData = (result: ICityState) => {
    paymentService.setAddressData(result);
  };

  render() {
    if (!this.state.model || !this.state.config) {
      return <div />;
    }

    const { model, cepNotFound, config } = this.state;

    model.province = model.province || 'none';
    model.city = model.city || 'none';
    model.number = model.number || null;

    return (
      <div>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={12}>
            <SearchCEP onClose={this.cepClosed} isFullScreen={true} />
            <FieldText
              id="postalCode"
              label={<Translate term={config.ead ? 'CEP da Residência do Aluno' : 'CEP da Residência'} />}
              validation="required|min:9|max:9"
              value={model.postalCode}
              mask="postalCode"
              inputProps={{
                pattern: '[0-9]*',
                inputMode: 'numeric',
                className: 'theme-input',
                type: 'tel'
              }}
              InputLabelProps={{
                className: 'theme-label',
                id: 'label-postal-code'
              }}
              onChange={this.setPostalCode}
            />
            {cepNotFound ? (
              <span className={'cepNotFound'}>
                <SvgIcon name={'warning'} />
                Ops! Não localizamos o CEP {this.state.model.postalCode}, verifique se está correto, se estiver correto continue preenchendo
                normalmente.
              </span>
            ) : null}
          </Grid>
        </Grid>

        <Grid container spacing={1} className="chk-userAddress__container">
          <Grid item xs={9}>
            <FieldText
              id="street"
              label={<Translate term="Endereço" />}
              validation="required"
              value={model.street}
              inputProps={{
                className: 'theme-input'
              }}
              InputLabelProps={{
                className: 'theme-label',
                id: 'label-street'
              }}
              onChange={this.setStreet}
            />
          </Grid>

          <Grid item xs={3}>
            <FieldText
              id="number"
              label={<Translate term="Número" />}
              validation="required"
              value={model.number}
              inputProps={{
                className: 'theme-input'
              }}
              inputRef={this.numberInputRef}
              InputLabelProps={{
                className: 'theme-label',
                id: 'label-address-number'
              }}
              onChange={this.setNumber}
            />
          </Grid>

          <Grid item xs={6} className="chk-userAddress__district">
            <FieldText
              id="district"
              label={<Translate term="Bairro" />}
              validation="required"
              value={model.district}
              inputProps={{
                className: 'theme-input'
              }}
              InputLabelProps={{
                className: 'theme-label',
                id: 'label-district'
              }}
              onChange={this.setDistrict}
            />
          </Grid>

          <Grid item xs={6} className="chk-userAddress__complement">
            <FieldText
              id="complement"
              label={<Translate term="Complemento" />}
              value={model.complement}
              inputProps={{
                className: 'theme-input'
              }}
              InputLabelProps={{
                className: 'theme-label',
                id: 'label-complement'
              }}
              onChange={this.setComplement}
            />
          </Grid>
        </Grid>

        <CityState
          disableAutocompleteOnProvince
          value={{ city: model.city, province: model.province }}
          onChange={this.setAddressData}
          templateId={config.template.id}
        />
      </div>
    );
  }
}
